import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialog } from '@angular/material/dialog';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { finalize } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Subscription } from 'rxjs';
import { SystemInfoService } from '../../../services/system-info.service';
import { KundeKortService, CUSTOMER_CATEGORIES, CUSTOMER_TYPES, CONTACT_ROLES, LEAD_SOURCES, leaseStatus, LEASE_WARN_DAYS } from '../../../services/kunde-kort.service';
import { AuthService } from '../../../services/auth.service';
import { NotificationService } from '../../../shared/notification.service';
import { BrregService } from '../../../services/brreg.service';
import { EmailService } from '../../../services/email.service';
import { CompanyUserService } from '../../../admin-setting/assing-company-users/company-user.service';
import { CrmTaskService } from '../../../services/crm-task.service';
import { PortalAdminService } from '../../../services/portal-admin.service';
import { AvtalelinjeService, AVTALE_KINDS, Avtalelinje } from '../../../services/avtalelinje.service';
import { MembershipV2Service } from '../../../services/membership-v2.service';
import { AvstemmingService } from '../../../services/avstemming.service';
import { resolveGarantiPayer } from '../../../services/garantiomsetning.service';
import { CompanysitesService } from '../../company-sites/company-sites.service';
import { openSystemConfigPopup, isServiceModeActive } from '../../company-sites/system-config-popup';
import { GroupDialogComponent } from '../../membership-v2/dialogs/group-dialog.component';
import { combineLatest } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
@Component({
  selector: 'add-notater-dialog',
  templateUrl: 'add-notater.html'
})
export class AddNotaterDialog {
  data = inject(MAT_DIALOG_DATA);
  notaterForm: FormGroup;

  constructor(private dialog: MatDialog, private fb: FormBuilder, private kundekortinfoService: KundeKortService, private notifier: NotificationService, private emailService: EmailService) {
    this.notaterForm = this.fb.group({
      text: ['', Validators.required],
      reminderDate: [null],
      reminderTime: ['12:00'],
    });

  }

  saveNote(): void {
    if (this.notaterForm.invalid) {
      this.notaterForm.markAllAsTouched();
      return;
    }

    const { reminderTime, ...formValue } = this.notaterForm.value;
    if (formValue.reminderDate && reminderTime) {
      const [hour, minute] = reminderTime.split(':').map(Number);
      formValue.reminderDate = moment(formValue.reminderDate).set({ hour, minute, second: 0, millisecond: 0 }).toDate();
    }

    const note = { kundekortId: this.data.kundekortId, ...formValue, createdBy: this.data.createdBy || null, createdAt: new Date() };
    this.kundekortinfoService.saveNotes(note)
      .then(() => {
        this.scheduleReminderEmail(note);
        this.dialog.closeAll();
      })
      .catch(() => this.notifier.showNotification('error', 'Could not save the note'));
  }

  private scheduleReminderEmail(note: any): void {
    if (!note.reminderDate || !this.data.createdBy) return;
    this.emailService.sendEmail({
      emailTo: this.data.createdBy,
      subject: `Reminder: ${this.data.kundeName || 'Customer'}`,
      title: '',
      html: '',
      text: `Reminder for ${this.data.kundeName || 'customer'}:\n\n${note.text}`,
      includeSignature: true,
      signatureType: 'frigg',
      asap: false,
      sendAtUnix: moment(note.reminderDate).unix(),
      attachments: [],
      kundekortId: this.data.kundekortId || null,
    }).catch(err => {
      console.error('Error scheduling reminder email', err);
      const pastDate = err?.message === 'Send time must be in the future';
      this.notifier.showNotification('error', pastDate
        ? 'Note saved, but the reminder was not scheduled — the date has already passed'
        : 'Note saved, but the reminder could not be scheduled');
    });
  }

}

@Component({
  selector: 'add-task-dialog',
  templateUrl: 'add-task.html'
})
export class AddTaskDialog {
  data = inject(MAT_DIALOG_DATA);
  taskForm: FormGroup;
  owners: any[] = [];

  constructor(private dialog: MatDialog, private fb: FormBuilder, private taskService: CrmTaskService, private notifier: NotificationService, private companyUserService: CompanyUserService) {
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      dueDate: [null, Validators.required],
      assigneeId: [this.data.defaultAssigneeId || null, Validators.required],
    });
    this.companyUserService.getUsers$().subscribe(users => this.owners = users as any[]);
  }

  ownerLabel(owner: any): string {
    return owner?.['Name'] || owner?.['email'] || 'Unknown user';
  }

  saveTask(): void {
    if (this.taskForm.invalid) {
      this.taskForm.markAllAsTouched();
      return;
    }
    const value = this.taskForm.value;
    const assignee = this.owners.find(o => o.userId === value.assigneeId);
    this.taskService.addTask({
      kundekortId: this.data.kundekortId,
      title: value.title,
      dueDate: value.dueDate,
      assigneeId: value.assigneeId,
      assigneeName: assignee ? this.ownerLabel(assignee) : null,
      createdBy: this.data.createdBy || null,
    }).then(() => this.dialog.closeAll())
      .catch(() => this.notifier.showNotification('error', 'Could not save the task'));
  }

}

@Component({
  selector: 'add-contact-dialog',
  templateUrl: 'add-contact.html',
  styleUrls: ['kunde-kort-info-popup.css']
})
// Contact book dialog. Opened from a kundekort it starts with a search over
// every contact (link an existing person to this card, or create a new one);
// from the Contacts page (no kundekortId) it edits the person and the cards
// they are linked to.
export class AddContactPersonDialog implements OnDestroy {
  data = inject(MAT_DIALOG_DATA);
  contactsForm: FormGroup;
  roleOptions = CONTACT_ROLES;
  kundekortId: string = this.data.kundekortId || null;
  isEdit = !!this.data.contact;
  // Without a card (Contacts page) there is nothing to link to, so go straight to the form.
  step: 'search' | 'link' | 'form' = this.isEdit || !this.kundekortId ? 'form' : 'search';

  search = '';
  results: any[] = [];
  picked: any = null;
  linkRoles: string[] = this.data.presetRoles || [];
  saving = false;

  // Live copy of the person being edited, so links added/removed show at once.
  contact: any = this.data.contact || null;
  linkedCards: { id: string, name: string }[] = [];
  cardSearch = '';
  cardOptions: { id: string, name: string }[] = [];
  emailMatch: any = null;

  private all: any[] = [];
  private customerNames: { [id: string]: string } = {};
  private sub: Subscription;

  constructor(private dialog: MatDialog, private fb: FormBuilder, private kundekortinfoService: KundeKortService, private notifier: NotificationService) {
    const c = this.data.contact || {};
    this.contactsForm = this.fb.group({
      name: [c.name || '', Validators.required],
      phone: [c.phone || ''],
      email: [c.email || '', Validators.email],
      stilling: [c.stilling || ''],
      roles: [c.roles || this.data.presetRoles || []],
    });
    this.sub = combineLatest([this.kundekortinfoService.getAllContacts$(), this.kundekortinfoService.getCustomers()])
      .subscribe(([contacts, customers]) => {
        this.customerNames = {};
        (customers as any[]).forEach(k => this.customerNames[k.kundekortId] = k.name || '(unnamed)');
        this.all = (contacts as any[])
          .map(x => ({ ...x, cards: (x.kundekortIds || []).map(id => this.customerNames[id] || id) }))
          .sort((a, b) => String(a.name || '').trim().localeCompare(String(b.name || '').trim()));
        if (this.contact) {
          this.contact = this.all.find(x => x.dbid === this.contact.dbid) || this.contact;
          this.linkedCards = (this.contact.kundekortIds || []).map(id => ({ id, name: this.customerNames[id] || id }));
        }
        this.filter();
        this.filterCards();
      });
  }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  get otherCardCount(): number {
    return (this.contact?.kundekortIds || []).filter(id => id !== this.kundekortId).length;
  }

  filter() {
    const q = this.search.trim().toLowerCase();
    const hit = (x: any) => [x.name, x.email, x.phone, x.stilling, x.company, ...x.cards]
      .some(v => String(v || '').toLowerCase().includes(q));
    this.results = (q ? this.all.filter(hit) : this.all).slice(0, 50);
  }

  isLinkedHere(x: any): boolean {
    return !!this.kundekortId && (x.kundekortIds || []).includes(this.kundekortId);
  }

  pick(x: any) {
    if (this.isLinkedHere(x)) return;
    this.picked = x;
    this.step = 'link';
  }

  startNew() {
    const q = this.search.trim();
    this.contactsForm.patchValue(q.includes('@') ? { email: q } : { name: q });
    this.checkEmail();
    this.step = 'form';
  }

  // Warn before creating a second record for a person already in the book.
  checkEmail() {
    if (this.isEdit) return;
    const e = String(this.contactsForm.value.email || '').trim().toLowerCase();
    this.emailMatch = e ? this.all.find(x => String(x.email || '').trim().toLowerCase() === e) || null : null;
  }

  linkPicked() {
    if (!this.picked) return;
    this.saving = true;
    this.kundekortinfoService.linkContact(this.picked, this.kundekortId, this.linkRoles)
      .then(() => this.dialog.closeAll())
      .catch(() => this.notifier.showNotification('error', 'Could not link the contact'))
      .finally(() => this.saving = false);
  }

  saveContacts(): void {
    if (this.contactsForm.invalid) {
      this.contactsForm.markAllAsTouched();
      return;
    }
    const { roles, ...person } = this.contactsForm.value;
    this.saving = true;
    let save: Promise<any>;
    if (!this.isEdit && !this.kundekortId) {
      // Contacts page: stay open on the new person so customers can be linked.
      this.kundekortinfoService.createContact(person)
        .then(ref => { this.contact = { ...person, dbid: ref.id, kundekortIds: [] }; this.isEdit = true; this.filterCards(); })
        .catch(() => this.notifier.showNotification('error', 'Could not save the contact'))
        .finally(() => this.saving = false);
      return;
    }
    if (!this.isEdit) {
      save = this.kundekortinfoService.createContact(person, this.kundekortId, roles);
    } else {
      save = this.kundekortinfoService.updateContactPerson(this.contact.dbid, person);
      if (this.kundekortId) {
        save = save.then(() => this.kundekortinfoService.setContactRoles(this.contact, this.kundekortId, roles));
      }
    }
    save
      .then(() => this.dialog.closeAll())
      .catch(() => this.notifier.showNotification('error', 'Could not save the contact'))
      .finally(() => this.saving = false);
  }

  // ---- linked cards (Contacts page) ----
  filterCards() {
    const q = this.cardSearch.trim().toLowerCase();
    const linked = new Set(this.contact?.kundekortIds || []);
    const all = Object.keys(this.customerNames)
      .filter(id => !linked.has(id))
      .map(id => ({ id, name: this.customerNames[id] }));
    this.cardOptions = (q ? all.filter(c => c.name.toLowerCase().includes(q)) : all)
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 50);
  }

  addCard(card: { id: string, name: string }) {
    this.cardSearch = '';
    this.kundekortinfoService.linkContact(this.contact, card.id, [])
      .catch(() => this.notifier.showNotification('error', 'Could not link the customer'));
  }

  removeCard(card: { id: string, name: string }) {
    if (!confirm(`Remove ${this.contact.name || 'this contact'} from ${card.name}?`)) return;
    this.kundekortinfoService.unlinkContact(this.contact, card.id)
      .catch(() => this.notifier.showNotification('error', 'Could not remove the customer'));
  }

}


@Component({
  selector: 'add-drift-avtale-dialog',
  templateUrl: 'add-drift-avtale.html'
})
export class AddDriftAvtaleDialog {
  data = inject(MAT_DIALOG_DATA);
  avtaleForm: FormGroup;

  constructor(private dialog: MatDialog, private fb: FormBuilder, private kundekortinfoService: KundeKortService, private notifier: NotificationService) {
    this.avtaleForm = this.fb.group({
      text: ['', Validators.required],
    });
  }

  saveAvtale(): void {
    if (this.avtaleForm.invalid) {
      this.avtaleForm.markAllAsTouched();
      return;
    }
    this.kundekortinfoService.saveDriftAvtale({ kundekortId: this.data.kundekortId, ...this.avtaleForm.value, createdBy: this.data.createdBy || null, createdAt: new Date() })
      .then(() => this.dialog.closeAll())
      .catch((err) => {
        console.error('Could not save service agreement', err);
        this.notifier.showNotification('error', 'Could not save the service agreement: ' + (err?.message || err));
      });
  }

}


@Component({
  selector: 'add-mote-referat-dialog',
  templateUrl: 'add-mote-referat.html'
})
export class AddMoteReferatDialog {
  data = inject(MAT_DIALOG_DATA);
  referatForm: FormGroup;

  constructor(private dialog: MatDialog, private fb: FormBuilder, private kundekortinfoService: KundeKortService, private notifier: NotificationService) {
    this.referatForm = this.fb.group({
      meetingDate: [null, Validators.required],
      text: ['', Validators.required],
    });
  }

  saveReferat(): void {
    if (this.referatForm.invalid) {
      this.referatForm.markAllAsTouched();
      return;
    }
    this.kundekortinfoService.saveMoteReferat({ kundekortId: this.data.kundekortId, ...this.referatForm.value, createdBy: this.data.createdBy || null, createdAt: new Date() })
      .then(() => this.dialog.closeAll())
      .catch(() => this.notifier.showNotification('error', 'Could not save the meeting note'));
  }

}



type ActivityType = 'note' | 'meeting' | 'agreement' | 'task' | 'email';

const ACTIVITY_FILTERS: { key: 'all' | ActivityType, label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'note', label: 'Notes' },
  { key: 'meeting', label: 'Meetings' },
  { key: 'agreement', label: 'Agreements' },
  { key: 'task', label: 'Tasks' },
  { key: 'email', label: 'Emails' },
];

// Settings are stored as "true"/"false" strings on some system docs, booleans on others.
const isOn = (v: any): boolean => v === true || String(v).toLowerCase() === 'true';

// Size for the add/edit dialogs opened from the card (note, task, meeting, agreement, contact).
const SUB_DIALOG = { width: '720px', maxWidth: '95vw' };

// Driftskostnad per system per month, ex. VAT — mirrors driftkostnad.service.ts.
const DRIFT_FEE_EX_VAT = 59;

@Component({
  selector: 'kunde-kort-info-popup',
  templateUrl: './kunde-kort-info-popup.html',
  styleUrls: ['./kunde-kort-info-popup.css']
})
export class KundekortInfoPopup implements OnInit, OnDestroy {
  dialog = inject(MatDialog);
  categories = CUSTOMER_CATEGORIES;
  customerTypes = CUSTOMER_TYPES;
  leadSources = LEAD_SOURCES;
  owners: any[] = [];
  currentUserEmail: string = null;
  currentUserId: string = null;
  isSuperAdmin = false;
  // PowerOffice customer (Info tab). `po` is the last preview from the server.
  po: any = null;
  poBusy = false;

  // ---- details pane (read view <-> one edit form) ----
  edit: any = {};
  editingDetails = false;
  savingInfo = false;
  orgLookupLoading = false;

  existingContacts: any[] = [];
  // Loaded via the super-admin-gated /portalAdmin endpoint; hidden otherwise.
  portalUsers: any[] = [];
  portalLoaded = false;

  // ---- systems ----
  connectedSystems: any[] = [];
  availableSystems: any[] = [];
  selectedSystemId: string = null;
  systemSearch = '';
  filteredSystems: any[] = [];
  expandedSystem: { [key: string]: boolean } = {};
  // Systems whose garanti shortfall this customer pays without being linked.
  garantiElsewhere: any[] = [];
  activeSystemCount = 0;
  private allSystems: any[] = [];
  private systemLinks: any[] = [];
  private customers: any[] = [];
  private customerNames: { [id: string]: string } = {};
  private daysSinceLastSale: { [systemId: string]: number | null } = {};

  // Systems whose lease has ended or ends within LEASE_WARN_DAYS.
  leaseAlerts: any[] = [];
  invoiceContacts: any[] = [];
  leaseWarnDays = LEASE_WARN_DAYS;
  datesDraft: { [linkId: string]: { install: string, kickoff: string, leaseEnd: string } } = {};
  datesSaving: { [linkId: string]: boolean } = {};

  // ---- groups ----
  groups: any[] = [];
  linkableGroups: any[] = [];
  selectedGroupId: string = null;
  totalMembers = 0;
  private rawGroups: any[] = [];
  private memberCounts: { [groupDbid: string]: number } = {};
  private sitesMap: { [siteId: string]: { name: string, companyId: string } } = {};

  // ---- billing ----
  billingHistory: any[] = [];
  billingLoaded = false;
  expandedMonth: { [key: string]: boolean } = {};
  lastBilled: any = null;
  driftFeeMonthly = 0;

  // Recurring contract charges (rent, driftstøtte, service). They live per
  // SYSTEM; editing here and in Billing > Avtaler writes the same records.
  avtaleKinds = AVTALE_KINDS;
  avtaleBySystem: { [systemId: string]: Avtalelinje[] } = {};
  avtaleMonthlyBySystem: { [systemId: string]: number } = {};
  avtaleMonthlyTotal = 0;
  avtaleDraft: { [systemId: string]: any } = {};
  avtaleOpen: { [systemId: string]: boolean } = {};
  avtaleSaving: { [key: string]: boolean } = {};
  avtaleError: { [systemId: string]: string } = {};
  private avtalelinjer: Avtalelinje[] = [];

  // ---- activity ----
  activityFilters = ACTIVITY_FILTERS;
  activityFilter: 'all' | ActivityType = 'all';
  activityFeed: any[] = [];
  filteredActivity: any[] = [];
  activityCounts: { [key: string]: number } = {};
  openTasks: any[] = [];

  attachments: { name: string; url: string; dbid?: string }[] = [];
  uploadProgress = 0;

  // ---- organisation tree: one level, parent -> units ----
  // A unit is still its own invoice recipient; the link only groups cards.
  parent: any = null;
  units: any[] = [];
  unitTotals = { systems: 0, groups: 0, contract: 0, lastBilled: 0 };
  parentSuggestions: { id: string, name: string, reason: string }[] = [];
  parentOptions: any[] = [];
  unitOptions: any[] = [];
  choosingParent = false;
  selectedParentId: string = null;
  selectedUnitId: string = null;
  savingOrg = false;
  brregUnits: any[] = [];
  brregUnitsState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  private brregParentOrg: string | null = null;
  private avstemmingDocs: any[] = [];

  private subs: Subscription[] = [];

  constructor(
    private storage: AngularFireStorage,
    private kundeKortService: KundeKortService,
    private systemInfoService: SystemInfoService,
    private auth: AuthService,
    private notifier: NotificationService,
    private companyUserService: CompanyUserService,
    private taskService: CrmTaskService,
    private emailService: EmailService,
    private brregService: BrregService,
    private portalAdmin: PortalAdminService,
    private avtaleSvc: AvtalelinjeService,
    private membershipSvc: MembershipV2Service,
    private avstemming: AvstemmingService,
    private siteService: CompanysitesService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<KundekortInfoPopup>
  ) { }

  get id(): string { return this.data.kundekortId; }

  ngOnInit() {
    this.resetEdit();
    this.subs.push(this.auth.user$.subscribe(u => { this.currentUserEmail = u?.email || null; this.currentUserId = u?.uid || null; }));
    this.subs.push(this.companyUserService.getUsers$().subscribe(users => this.owners = users as any[]));
    if (!this.id) return;
    this.loadSystemsAndGroups();
    this.loadActivity();
    this.loadBillingHistory();
    this.loadPortalUsers();
    this.subs.push(this.kundeKortService.getContacts(this.id).subscribe(list => {
      this.existingContacts = list as any[];
      this.invoiceContacts = this.existingContacts.filter(c => (c.roles || []).includes('Invoice recipient'));
    }));
    this.subs.push(this.kundeKortService.getAttachments(this.id).subscribe(list => {
      this.attachments = list.map(att => ({ name: att['name'], url: att['url'], dbid: att['dbid'] }));
    }));
    this.subs.push(this.avtaleSvc.list$().subscribe(list => {
      this.avtalelinjer = list;
      this.recomputeAvtaler();
      this.rebuildOrg();
    }));
    this.loadBrregParent();
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  onNoClick() {
    this.dialogRef.close();
  }

  // ======================================================================
  // Details
  // ======================================================================

  ownerLabel(owner: any): string {
    return owner?.['Name'] || owner?.['email'] || 'Unknown user';
  }

  get customerInitials(): string {
    const name = (this.data?.name || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  get vatLabel(): string {
    const r = this.data.vatRate;
    if (r == null) return '15 % (default)';
    const pct = Number(r) > 1 ? Number(r) : Number(r) * 100;
    return `${+pct.toFixed(2)} %`;
  }

  get addressLine(): string {
    const d = this.data;
    const zipCity = [d.zipCode, d.city].filter(Boolean).join(' ');
    return [d.address, d.address2, zipCity, d.country].filter(Boolean).join(', ');
  }

  private resetEdit() {
    const d = this.data;
    this.edit = {
      name: d.name || '', legalName: d.legalName || '', email: d.email || '', phone: d.phone || '',
      web: d.web || '', orgId: d.orgId || '', customerType: d.customerType || null, category: d.category || null,
      address: d.address || '', address2: d.address2 || '', zipCode: d.zipCode || '', city: d.city || '', country: d.country || '',
      leadSource: d.leadSource || null, ownerId: d.ownerId || null,
      // Whole percent in the form; stored as a decimal (0.15).
      vatPercent: d.vatRate != null ? (Number(d.vatRate) > 1 ? Number(d.vatRate) : Number(d.vatRate) * 100) : 15,
      ourReference: d.ourReference || '',
      customerReference: d.customerReference || '',
      powerOfficeCustomerNo: d.powerOfficeCustomerNo || '',
      invoiceLinesByGroup: d.invoiceLinesByGroup === true,
      kickbackPercent: d.kickbackRate != null ? Number(d.kickbackRate) * 100 : null,
      // Opt-OUT: charged unless explicitly false.
      driftkostnaderEnabled: d.driftkostnaderEnabled !== false,
    };
  }

  startEditDetails() {
    this.resetEdit();
    this.editingDetails = true;
  }

  cancelEditDetails() {
    this.resetEdit();
    this.editingDetails = false;
  }

  saveInfo() {
    if (!this.id) return;
    this.savingInfo = true;
    const fields = ['name', 'legalName', 'email', 'phone', 'web', 'orgId', 'customerType', 'category', 'address', 'address2', 'zipCode', 'city', 'country', 'leadSource', 'ownerId', 'ourReference', 'customerReference', 'powerOfficeCustomerNo', 'invoiceLinesByGroup'];
    const payload: any = {};
    fields.forEach(f => payload[f] = this.edit[f] ?? null);
    const vat = Number(this.edit.vatPercent);
    payload.vatRate = isFinite(vat) ? vat / 100 : 0.15;
    const kick = this.edit.kickbackPercent != null && this.edit.kickbackPercent !== '' ? Number(this.edit.kickbackPercent) : null;
    payload.kickbackRate = kick != null && isFinite(kick) ? kick / 100 : null;
    payload.driftkostnaderEnabled = !!this.edit.driftkostnaderEnabled;
    const owner = this.owners.find(o => o.userId === payload.ownerId);
    payload.ownerName = owner ? this.ownerLabel(owner) : null;
    // A hand-typed Kundenr only counts as "invoiced to parent" if it is the parent's.
    if (payload.powerOfficeCustomerNo !== (this.data.powerOfficeCustomerNo ?? null)) {
      payload.invoiceToParent = !!payload.powerOfficeCustomerNo && !!this.parent
        && String(payload.powerOfficeCustomerNo) === String(this.parent.powerOfficeCustomerNo || '');
    }
    // Becoming Active (or getting its org. number while Active) is when a card
    // gets its PowerOffice customer — see KundeKortService.autoPowerOffice.
    const toPowerOffice = payload.category === 'Active' && !payload.powerOfficeCustomerNo
      && (this.data.category !== 'Active' || payload.orgId !== (this.data.orgId ?? null));
    this.kundeKortService.updateCustomer(this.id, payload)
      .then(() => {
        Object.assign(this.data, payload);
        if (toPowerOffice) {
          this.kundeKortService.autoPowerOffice(this.id).then(r => {
            if (!r) return;
            if (r.powerOfficeCustomerNo) Object.assign(this.data, { powerOfficeCustomerNo: r.powerOfficeCustomerNo, invoiceToParent: !!this.data.parentKundekortId });
            this.notifier.showNotification(r.type, r.message);
          });
        }
        this.savingInfo = false;
        this.editingDetails = false;
        this.recomputeDriftFee();
        this.notifier.showNotification('success', 'Customer updated');
      })
      .catch(err => { console.error('Error updating customer', err); this.savingInfo = false; this.notifier.showNotification('error', 'Could not save the changes'); });
  }

  lookupOrg() {
    if (this.orgLookupLoading || !this.edit.orgId) return;
    this.orgLookupLoading = true;
    this.brregService.lookup(this.edit.orgId)
      .then(result => {
        this.edit.name = result.name || this.edit.name;
        this.edit.address = result.address;
        this.edit.address2 = result.address2;
        this.edit.zipCode = result.zipCode;
        this.edit.city = result.city;
        this.edit.country = result.country;
      })
      .catch(err => this.notifier.showNotification('error', err?.message || 'Lookup failed'))
      .finally(() => this.orgLookupLoading = false);
  }

  addContactPerson() {
    this.dialog.open(AddContactPersonDialog, { ...SUB_DIALOG, data: { kundekortId: this.id } });
  }

  editContactPerson(contact: any) {
    this.dialog.open(AddContactPersonDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, contact } });
  }

  // Removes the person from this customer. If they are on no other customer
  // they would be left orphaned in the contact book, so offer to delete them.
  async removeExistingContact(c: any) {
    const name = c.name || c.email || 'this contact';
    const others = (c.kundekortIds || []).filter(id => id !== this.id).length;
    if (!confirm(`Remove ${name} from ${this.data.name || 'this customer'}?`)) return;
    try {
      if (!others && confirm(`${name} is not on any other customer. Delete ${name} from the contact book as well?\n\nOK = delete completely · Cancel = keep in the contact book`)) {
        await this.kundeKortService.deleteContact(c.dbid);
        this.notifier.showNotification('success', `${name} deleted`);
      } else {
        await this.kundeKortService.unlinkContact(c, this.id);
        this.notifier.showNotification('success', `${name} removed from ${this.data.name || 'this customer'}`);
      }
    } catch (err: any) {
      console.error('Error removing contact', err);
      this.notifier.showNotification('error', `Could not remove ${name}: ${err?.message || err}`);
    }
  }

  private async loadPortalUsers() {
    try {
      const user: any = await this.auth.getUserPromise();
      this.isSuperAdmin = this.auth.isSuperAdmin(user);
      if (!this.isSuperAdmin) return;
      this.portalUsers = await this.portalAdmin.listUsers(this.id);
    } catch (err) {
      console.error('Error loading portal users', err);
    }
    this.portalLoaded = true;
  }

  // lastLogin/createdAt arrive as unix seconds; be lenient about ms.
  portalWhen(value: any): string {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '';
    return moment.unix(n > 1e12 ? Math.floor(n / 1000) : n).format('D MMM YYYY');
  }

  // ======================================================================
  // Systems + groups
  // ======================================================================

  private loadSystemsAndGroups() {
    this.membershipSvc.getSitesMapOnce()
      .then(m => { this.sitesMap = m; this.rebuildSystems(); this.rebuildGroups(); })
      .catch(err => console.error('Error loading sites', err));

    this.subs.push(combineLatest([
      this.systemInfoService.systemInfo(),
      this.kundeKortService.getAllSystemKunder$(),
      this.kundeKortService.getCustomers(),
    ]).subscribe(([systems, links, customers]) => {
      this.allSystems = systems as any[];
      this.systemLinks = links as any[];
      this.customers = (customers as any[]).slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      this.customerNames = {};
      this.customers.forEach(c => this.customerNames[c.kundekortId] = c.name || '(unnamed)');
      this.rebuildSystems();
      this.rebuildGroups();
      this.rebuildOrg();
    }));

    this.subs.push(this.membershipSvc.getAllGroupsLive$().subscribe(groups => {
      this.rawGroups = groups;
      this.rebuildGroups();
      this.rebuildOrg();
    }));
  }

  private findSystem(id: string): any {
    return this.allSystems.find(s => s.systemId == id || s.dbid == id);
  }

  private systemIdsOf(system: any, fallback: string): string[] {
    return Array.from(new Set([system?.systemId, system?.dbid, fallback].filter(Boolean).map(String)));
  }

  private rebuildSystems() {
    const me = this.id;
    const liveCustomers = new Set(Object.keys(this.customerNames));
    const myLinks = this.systemLinks.filter(l => l['kundekortId'] === me);
    const linkedIds = new Set<string>();

    this.connectedSystems = myLinks.map(link => {
      const system = this.findSystem(link['systemId']);
      const ids = this.systemIdsOf(system, link['systemId']);
      ids.forEach(i => linkedIds.add(i));
      // avtaleKey is the canonical id Billing > Avtaler writes; avtaleKeys
      // covers every spelling a link may carry, for reading existing lines.
      const canonical = String((system && system['systemId']) || link['systemId'] || '');
      const sharedWith = Array.from(new Set(this.systemLinks
        .filter(l => ids.includes(String(l['systemId'])) && l['kundekortId'] !== me)
        .map(l => l['kundekortId'])));
      const linkedKunder = Array.from(new Set(this.systemLinks
        .filter(l => ids.includes(String(l['systemId'])))
        .map(l => l['kundekortId'])));
      const payer = resolveGarantiPayer(system?.['garantiKundekortId'] || null, linkedKunder, liveCustomers);
      const site = this.sitesMap[system?.['siteId']];
      const lastSale = this.daysSinceLastSale[canonical];
      return {
        ...system,
        missing: !system,
        systemId: link['systemId'],
        docId: system?.['dbid'] || null,
        linkId: link['dbid'],
        avtaleKey: canonical,
        avtaleKeys: ids,
        ids,
        siteName: site ? site.name : null,
        canteen: isOn(system?.['kantineAvailble']),
        pay: {
          card: isOn(system?.['isCardActive']),
          vipps: isOn(system?.['isVippsActive']),
          credit: isOn(system?.['isCreditActive']),
          groupSubsidy: isOn(system?.['isGroupSubsidyActive']),
          guest: isOn(system?.['isGuestModeActive']),
          invoiceBySystem: isOn(system?.['invoiceBySystem']),
          serviceMode: isServiceModeActive(system),
        },
        daysSinceLastSale: lastSale,
        healthLoaded: lastSale !== undefined,
        sharedWith: sharedWith.map(k => this.customerNames[k] || k),
        garantiPayerLabel: payer.kundekortId === me ? 'This customer'
          : payer.kundekortId ? (this.customerNames[payer.kundekortId] || payer.kundekortId)
          : payer.payerSource === 'ambiguous' ? 'Not set — linked to several customers' : 'Not set',
        garantiPayerIsOther: !!payer.kundekortId && payer.kundekortId !== me,
        groupNames: [] as string[],
        leaseReminder: system?.['leaseReminder'] || null,
        dates: {
          install: system?.['installDate'] || '',
          kickoff: system?.['kickoffDate'] || '',
          leaseEnd: system?.['leaseEndDate'] || '',
        },
        lease: leaseStatus(system?.['leaseEndDate']),
      };
    }).sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));

    this.connectedSystems.forEach(s => this.loadSystemHealth(s));
    this.leaseAlerts = this.connectedSystems
      .filter(s => s.lease && s.lease.state !== 'ok')
      .sort((a, b) => a.lease.days - b.lease.days);
    this.activeSystemCount = this.connectedSystems.filter(s => s.active).length;

    this.availableSystems = this.allSystems
      .filter(s => !linkedIds.has(String(s.systemId)) && !linkedIds.has(String(s.dbid)))
      .sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
    this.filterAvailableSystems();

    this.garantiElsewhere = this.allSystems
      .filter(s => s['garantiKundekortId'] === me && !linkedIds.has(String(s.systemId)) && !linkedIds.has(String(s.dbid)));

    this.recomputeAvtaler();
    this.recomputeDriftFee();
    this.attachGroupsToSystems();
  }

  // One read per system for the lifetime of the popup — system_info is a live
  // feed of every system and re-emits on any change anywhere.
  private loadSystemHealth(system: any) {
    const key = system.avtaleKey;
    if (!key || key in this.daysSinceLastSale) return;
    this.daysSinceLastSale[key] = undefined;
    this.kundeKortService.getLastTransactionUnix(system.systemId).then((snap: any) => {
      const days = snap && !snap.empty
        ? Math.floor((Date.now() / 1000 - snap.docs[0].data()['unix']) / 86400)
        : null;
      this.daysSinceLastSale[key] = days;
      this.connectedSystems.filter(s => s.avtaleKey === key).forEach(s => { s.daysSinceLastSale = days; s.healthLoaded = true; });
    }).catch(err => console.error('Error loading system health', err));
  }

  toggleSystem(s: any) {
    this.expandedSystem[s.linkId] = !this.expandedSystem[s.linkId];
  }

  editSystem(s: any, event?: Event) {
    event?.stopPropagation();
    if (!s.docId) return;
    openSystemConfigPopup(this.dialog, this.siteService, s.siteId, s.docId)
      .catch(err => { console.error('Error opening system settings', err); this.notifier.showNotification('error', 'Could not open the system settings'); });
  }

  systemLabel(s: any): string {
    const site = this.sitesMap[s?.['siteId']];
    return `#${s.number} — ${s.name}` + (site && site.name !== s.name ? ` (${site.name})` : '');
  }

  // Matches number, system name and site name so nobody needs to know the machine number.
  filterAvailableSystems() {
    const q = this.systemSearch.trim().toLowerCase();
    this.filteredSystems = (q
      ? this.availableSystems.filter(s => this.systemLabel(s).toLowerCase().includes(q))
      : this.availableSystems
    ).map(s => ({ id: s.systemId || s.dbid, label: this.systemLabel(s) }));
  }

  systemOptionLabel = (v: any) => (v && typeof v === 'object' ? v.label : v) || '';

  // The autocomplete writes the picked option object into the model; typing writes a string.
  onSystemSearch(value: any) {
    if (value && typeof value === 'object') {
      this.selectedSystemId = value.id;
      this.systemSearch = value.label;
      return;
    }
    this.selectedSystemId = null;
    this.filterAvailableSystems();
  }

  addSystem() {
    if (!this.selectedSystemId) return;
    this.kundeKortService.addSystemKunde(this.selectedSystemId, this.id)
      .then(() => { this.selectedSystemId = null; this.systemSearch = ''; this.filterAvailableSystems(); })
      .catch(err => { console.error('Error linking system', err); this.notifier.showNotification('error', 'Could not connect the system'); });
  }

  removeSystem(s: any, event?: Event) {
    event?.stopPropagation();
    if (!confirm(`Disconnect system #${s.number || '?'} ${s.name || ''} from this customer? Its purchases, contract charges and driftskostnader will no longer be billed here.`)) return;
    this.kundeKortService.removeSystemKunde(s.linkId)
      .catch(err => { console.error('Error unlinking system', err); this.notifier.showNotification('error', 'Could not disconnect the system'); });
  }

  private rebuildGroups() {
    const me = this.id;
    const typeOf = (g: any) => g.worker === 'subsidyGroup' ? 'Subsidy' : 'Credit';
    this.groups = this.rawGroups
      .filter(g => g.customerId === me)
      .map(g => {
        const site = this.sitesMap[g.siteId];
        const systemIds: string[] = Array.isArray(g.systemIds) ? g.systemIds : [];
        return {
          ...g,
          siteName: site ? site.name : (g.siteId || '—'),
          typeLabel: typeOf(g),
          amount: g.worker === 'subsidyGroup' ? Number(g.subsidy) || 0 : Number(g.credit) || 0,
          systemLabels: systemIds.map(id => {
            const s = this.findSystem(id);
            return s ? `#${s.number} ${s.name || ''}`.trim() : id;
          }),
          memberCount: this.memberCounts[g.dbid],
        };
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    this.groups.forEach(g => this.loadMemberCount(g));
    this.recomputeTotalMembers();

    this.linkableGroups = this.rawGroups
      .filter(g => !g.customerId && g.isTest !== true)
      .map(g => ({ dbid: g.dbid, label: `${g.name || g.groupId} — ${this.sitesMap[g.siteId]?.name || g.siteId || 'no site'}` }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.attachGroupsToSystems();
  }

  private loadMemberCount(g: any) {
    if (g.dbid in this.memberCounts) return;
    this.memberCounts[g.dbid] = undefined;
    this.membershipSvc.countGroupMembers(g).then(n => {
      this.memberCounts[g.dbid] = n;
      this.groups.filter(x => x.dbid === g.dbid).forEach(x => x.memberCount = n);
      this.recomputeTotalMembers();
    }).catch(err => console.error('Error counting members', err));
  }

  private recomputeTotalMembers() {
    this.totalMembers = this.groups.reduce((sum, g) => sum + (Number(g.memberCount) || 0), 0);
  }

  private attachGroupsToSystems() {
    this.connectedSystems.forEach(s => {
      s.groupNames = this.groups
        .filter(g => (g.systemIds || []).some((id: string) => s.ids.includes(String(id))))
        .map(g => g.name || g.groupId);
    });
  }

  editGroup(g: any) {
    const systems = this.allSystems
      .filter(s => s.siteId === g.siteId)
      .map(s => ({ systemId: s.systemId || s.dbid, name: `#${s.number} ${s.name || ''}`.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const raw = this.rawGroups.find(x => x.dbid === g.dbid) || g;
    const ref = this.dialog.open(GroupDialogComponent, {
      width: '520px',
      data: { mode: 'edit', group: raw, customers: this.customers, systems }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'edit') {
        this.membershipSvc.updateGroup(g.dbid, res.patch)
          .then(() => this.membershipSvc.syncRenewalTask(g.dbid, { ...raw, ...res.patch }, true))
          .catch(() => { });
      }
    });
  }

  linkGroup() {
    const g = this.rawGroups.find(x => x.dbid === this.selectedGroupId);
    if (!g) return;
    const patch = { customerId: this.id };
    this.membershipSvc.updateGroup(g.dbid, patch)
      .then(() => this.membershipSvc.syncRenewalTask(g.dbid, { ...g, ...patch }))
      .then(() => this.selectedGroupId = null)
      .catch(() => { });
  }

  unlinkGroup(g: any) {
    if (!confirm(`Remove group "${g.name}" from this customer? Its subsidy and credit will no longer be invoiced here.`)) return;
    const raw = this.rawGroups.find(x => x.dbid === g.dbid) || g;
    const patch = { customerId: null };
    this.membershipSvc.updateGroup(g.dbid, patch)
      .then(() => this.membershipSvc.syncRenewalTask(g.dbid, { ...raw, ...patch }))
      .catch(() => { });
  }



  // ======================================================================
  // System dates (montering, kick-off, lease end) + lease reminder
  // ======================================================================


  // ======================================================================
  // PowerOffice customer (powerOfficeCustomer.js)
  // ======================================================================

  checkPowerOffice() {
    this.poBusy = true;
    this.kundeKortService.powerOfficeCustomer(this.id, 'preview')
      .then(res => this.po = res)
      .catch(err => this.notifier.showNotification('error', err?.error?.message || 'Could not reach PowerOffice'))
      .finally(() => this.poBusy = false);
  }

  // Sub-unit invoiced to its parent: copy the parent's Kundenr.
  invoiceToParent() {
    if (!this.parent) return;
    this.runPowerOffice('link-parent', `Invoice ${this.data.name} to ${this.parent.name}? Its invoices will post to ${this.parent.name}'s Kundenr in PowerOffice.`);
  }

  createInPowerOffice() {
    const c = this.po?.customer;
    if (!c) return;
    const what = c.action === 'create'
      ? `Create "${c.name}" as a new customer in PowerOffice`
      : `Link this card to the existing PowerOffice customer ${c.number} ${c.name}`;
    this.runPowerOffice('create', `${what}?`);
  }

  updatePowerOffice() {
    this.runPowerOffice('update', `Update customer ${this.data.powerOfficeCustomerNo} in PowerOffice with this card's name, org. number, invoice email and address?`);
  }

  private async runPowerOffice(action: 'link-parent' | 'create' | 'update', question: string) {
    this.poBusy = true;
    try {
      const env = this.po?.environment || (await this.kundeKortService.powerOfficeCustomer(this.id, 'preview')).environment;
      const prod = env === 'production';
      if (!confirm(`${question}\n\nPowerOffice ${env.toUpperCase()}${prod ? '' : ' — test company, nothing is saved on the card'}.`)) return;
      const res = await this.kundeKortService.powerOfficeCustomer(this.id, action, env);
      if (action === 'link-parent') {
        Object.assign(this.data, { powerOfficeCustomerNo: res.powerOfficeCustomerNo, invoiceToParent: true });
        this.notifier.showNotification('success', `Invoiced to ${this.parent.name} (Kundenr ${res.powerOfficeCustomerNo})`);
      } else if (action === 'create') {
        if (res.saved) Object.assign(this.data, { powerOfficeCustomerNo: String(res.customer.number), invoiceToParent: false });
        this.notifier.showNotification('success', `${res.customer.action === 'created' ? 'Created' : 'Linked to'} PowerOffice customer ${res.customer.number}${res.saved ? '' : ' (demo, not saved)'}`);
      } else {
        this.notifier.showNotification('success', res.changed.length ? `Updated in PowerOffice: ${res.changed.join(', ')}` : 'PowerOffice already matches this card');
      }
      this.po = null;
    } catch (err) {
      this.notifier.showNotification('error', err?.error?.message || err?.message || 'PowerOffice request failed');
    } finally {
      this.poBusy = false;
    }
  }

  addInvoiceContact() {
    this.dialog.open(AddContactPersonDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, presetRoles: ['Invoice recipient'] } });
  }

  formatDate(d: string): string {
    return d ? moment(d, 'YYYY-MM-DD').format('D MMM YYYY') : '';
  }

  leaseLabel(s: any): string {
    if (!s.lease) return '';
    const d = s.lease.days;
    if (s.lease.state === 'ended') return `Lease ended ${-d} d ago`;
    return d === 0 ? 'Lease ends today' : `Lease ends in ${d} d`;
  }

  startEditDates(s: any, event?: Event) {
    event?.stopPropagation();
    this.datesDraft[s.linkId] = { ...s.dates };
  }

  cancelEditDates(s: any) {
    delete this.datesDraft[s.linkId];
  }

  async saveDates(s: any) {
    const d = this.datesDraft[s.linkId];
    if (!d || !s.docId) return;
    this.datesSaving[s.linkId] = true;
    const patch = { installDate: d.install || null, kickoffDate: d.kickoff || null, leaseEndDate: d.leaseEnd || null };
    try {
      await this.kundeKortService.updateSystemFields(s.docId, patch);
      await this.syncLeaseReminder(s, d.leaseEnd || null);
      delete this.datesDraft[s.linkId];
      this.notifier.showNotification('success', 'Dates saved');
    } catch (err) {
      console.error('Error saving system dates', err);
      this.notifier.showNotification('error', 'Could not save the dates');
    }
    this.datesSaving[s.linkId] = false;
  }

  // One reminder per system: an open CRM task (shows in My tasks and on this
  // card) due LEASE_WARN_DAYS before the lease ends, plus an email to the
  // account owner that day. Changing or clearing the date moves or removes both.
  private async syncLeaseReminder(s: any, leaseEnd: string | null) {
    const prev = s.leaseReminder || {};
    if ((prev.leaseEndDate || null) === leaseEnd && (prev.taskId || !leaseEnd)) return;

    if (prev.emailId) await this.emailService.cancelEmail(prev.emailId);
    const status = leaseStatus(leaseEnd);
    if (!leaseEnd || !status || status.state === 'ended') {
      if (prev.taskId) await this.taskService.deleteTask(prev.taskId).catch(() => { });
      await this.kundeKortService.updateSystemFields(s.docId, { leaseReminder: null });
      return;
    }

    const owner = this.owners.find(o => o.userId === this.data.ownerId);
    const assigneeId = owner ? owner.userId : this.currentUserId;
    const assigneeName = owner ? this.ownerLabel(owner) : (this.currentUserEmail || null);
    const emailTo = (owner && owner.email) || this.currentUserEmail;
    const label = `#${s.number || '?'} ${s.name || ''}`.trim();
    const endText = moment(leaseEnd, 'YYYY-MM-DD').format('D MMM YYYY');
    const remindAt = moment.tz(leaseEnd, 'YYYY-MM-DD', 'Europe/Oslo').subtract(1, 'month').hour(9);
    const due = remindAt.isAfter(moment()) ? remindAt.toDate() : new Date();
    const title = `Lease ends ${endText} — ${label} (${this.data.name || 'customer'})`;

    let taskId = prev.taskId || null;
    if (taskId) {
      await this.taskService.updateTask(taskId, { title, dueDate: due, assigneeId, assigneeName, status: 'open' }).catch(() => { taskId = null; });
    }
    if (!taskId) {
      const ref: any = await this.taskService.addTask({
        kundekortId: this.id, title, dueDate: due, assigneeId, assigneeName,
        createdBy: this.currentUserEmail || null, kind: 'leaseEnd', systemId: s.docId,
      });
      taskId = ref?.id || null;
    }

    let emailId: string | null = null;
    if (emailTo) {
      const soon = !remindAt.isAfter(moment().add(15, 'minutes'));
      const id = await this.emailService.sendEmail({
        emailTo,
        subject: `Lease ends ${endText}: ${label}, ${this.data.name || ''}`.trim(),
        title: '', html: '',
        text: `The lease for system ${label} at ${this.data.name || 'the customer'} ends on ${endText}.\n\nTime to talk to the customer about renewal or pick-up.`,
        includeSignature: true, signatureType: 'frigg',
        asap: soon, sendAtUnix: soon ? undefined : remindAt.unix(),
        attachments: [], kundekortId: this.id,
      }).catch(err => { console.error('Could not schedule lease email', err); return null; });
      emailId = typeof id === 'string' ? id : null;
    }

    await this.kundeKortService.updateSystemFields(s.docId, { leaseReminder: { leaseEndDate: leaseEnd, taskId, emailId } });
  }

  // ======================================================================
  // Organisation tree
  // ======================================================================

  private async loadBrregParent() {
    const org = String(this.data.orgId || '').replace(/\s+/g, '');
    if (!/^\d{9}$/.test(org)) return;
    try {
      const r = await this.brregService.lookup(org);
      this.brregParentOrg = r.parentOrgId;
      this.rebuildOrg();
    } catch { /* not in the register — suggestions fall back to org no./Kundenr */ }
  }

  private cleanOrg(v: any): string { return String(v || '').replace(/\s+/g, ''); }

  private rebuildOrg() {
    if (!this.customers.length) return;
    const me = this.id;
    const self = this.customers.find(c => c.kundekortId === me);
    if (self) this.data.parentKundekortId = self.parentKundekortId || null;
    const parentId = this.data.parentKundekortId || null;
    this.parent = parentId ? (this.customers.find(c => c.kundekortId === parentId) || { kundekortId: parentId, name: '(deleted card)' }) : null;

    const children = this.customers.filter(c => c.parentKundekortId === me);
    const thisMonth = moment().format('YYYY-MM');
    this.units = children.map(c => {
      const sysIds = this.systemLinks.filter(l => l['kundekortId'] === c.kundekortId).map(l => String(l['systemId']));
      const systems = sysIds.map(id => this.findSystem(id)).filter(Boolean);
      const keys = new Set<string>();
      systems.forEach(s => { if (s.systemId) keys.add(String(s.systemId)); if (s.dbid) keys.add(String(s.dbid)); });
      sysIds.forEach(id => keys.add(id));
      const contract = this.avtalelinjer
        .filter(l => keys.has(String(l.systemId)) && AvtalelinjeService.dueInMonth(l, thisMonth))
        .reduce((sum, l) => sum + (Number(l.amountExVat) || 0), 0);
      let last: any = null;
      this.avstemmingDocs.forEach(d => {
        const row = (d.customers || []).find((x: any) => x.customerId === c.kundekortId);
        const month = d.period?.month || '';
        if (!row) return;
        if (!last || month > last.month) last = { month, total: Number(row.billableTotal) || 0 };
        else if (month === last.month) last.total += Number(row.billableTotal) || 0;
      });
      return {
        ...c,
        systemLabels: systems.map(s => `#${s.number} ${s.name || ''}`.trim()),
        systemCount: sysIds.length,
        groupCount: this.rawGroups.filter(g => g.customerId === c.kundekortId).length,
        contract: +contract.toFixed(2),
        lastBilled: last,
      };
    }).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

    this.unitTotals = {
      systems: this.units.reduce((s, u) => s + u.systemCount, 0),
      groups: this.units.reduce((s, u) => s + u.groupCount, 0),
      contract: +this.units.reduce((s, u) => s + u.contract, 0).toFixed(2),
      lastBilled: +this.units.reduce((s, u) => s + (u.lastBilled ? u.lastBilled.total : 0), 0).toFixed(2),
    };

    // One level only: a parent can't have a parent, and a unit can't have units.
    const hasChildren = new Set(this.customers.filter(c => c.parentKundekortId).map(c => c.parentKundekortId));
    this.parentOptions = this.customers.filter(c => c.kundekortId !== me && !c.parentKundekortId);
    this.unitOptions = this.customers.filter(c => c.kundekortId !== me && !c.parentKundekortId && !hasChildren.has(c.kundekortId));

    this.parentSuggestions = [];
    if (!parentId && !children.length) {
      const myOrg = this.cleanOrg(this.data.orgId);
      const myKundenr = String(this.data.powerOfficeCustomerNo || '').trim();
      const add = (c: any, reason: string) => {
        if (!this.parentSuggestions.some(x => x.id === c.kundekortId)) this.parentSuggestions.push({ id: c.kundekortId, name: c.name || '(unnamed)', reason });
      };
      if (this.brregParentOrg) {
        this.parentOptions.filter(c => this.cleanOrg(c.orgId) === this.brregParentOrg)
          .forEach(c => add(c, 'Registered parent organisation in Brønnøysund'));
      }
      this.parentOptions.filter(c => hasChildren.has(c.kundekortId) && (
        (myOrg && this.cleanOrg(c.orgId) === myOrg) || (myKundenr && String(c.powerOfficeCustomerNo || '').trim() === myKundenr)))
        .forEach(c => add(c, 'Already a parent, same org no. or Kundenr'));
      this.parentSuggestions = this.parentSuggestions.slice(0, 3);
    }
  }

  openCard(kundekortId: string) {
    const c = this.customers.find(x => x.kundekortId === kundekortId);
    if (!c) return;
    this.dialog.open(KundekortInfoPopup, { width: '95vw', maxWidth: '1440px', height: '92vh', data: { ...c } });
  }

  setParent(parentId: string | null) {
    if (parentId === this.id) return;
    if (parentId && this.units.length) {
      this.notifier.showNotification('error', 'This card has its own units, so it cannot be placed under another card');
      return;
    }
    this.savingOrg = true;
    this.kundeKortService.updateCustomer(this.id, { parentKundekortId: parentId || null })
      .then(() => {
        this.data.parentKundekortId = parentId || null;
        this.choosingParent = false;
        this.selectedParentId = null;
        this.notifier.showNotification('success', parentId ? 'Parent organisation set' : 'Removed from parent organisation');
      })
      .catch(err => { console.error('Error setting parent', err); this.notifier.showNotification('error', 'Could not save'); })
      .finally(() => this.savingOrg = false);
  }

  removeParent() {
    if (!this.parent || !confirm(`Remove this card from "${this.parent.name}"? Nothing about invoicing changes.`)) return;
    this.setParent(null);
  }

  linkUnit() {
    const id = this.selectedUnitId;
    if (!id) return;
    this.savingOrg = true;
    this.kundeKortService.updateCustomer(id, { parentKundekortId: this.id })
      .then(() => { this.selectedUnitId = null; this.notifier.showNotification('success', 'Unit added'); })
      .catch(err => { console.error('Error linking unit', err); this.notifier.showNotification('error', 'Could not add the unit'); })
      .finally(() => this.savingOrg = false);
  }

  unlinkUnit(u: any) {
    if (!confirm(`Remove "${u.name}" from this organisation? It stays a customer of its own; nothing about invoicing changes.`)) return;
    this.kundeKortService.updateCustomer(u.kundekortId, { parentKundekortId: null })
      .catch(err => { console.error('Error unlinking unit', err); this.notifier.showNotification('error', 'Could not remove the unit'); });
  }

  async loadBrregUnits() {
    const org = this.cleanOrg(this.data.orgId);
    if (!/^\d{9}$/.test(org)) return;
    this.brregUnitsState = 'loading';
    try {
      const list = await this.brregService.listUnits(org);
      const cardByOrg = new Map(this.customers.filter(c => c.orgId).map(c => [this.cleanOrg(c.orgId), c]));
      this.brregUnits = list
        .map(u => {
          const card = cardByOrg.get(u.orgId);
          return { ...u, card: card || null, isUnit: !!card && card.parentKundekortId === this.id };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      this.brregUnitsState = 'done';
    } catch (err) {
      console.error('Error listing Brønnøysund units', err);
      this.brregUnitsState = 'error';
    }
  }

  // Brønnøysund names are upper case ("STABEKK VIDEREGÅENDE SKOLE").
  private niceName(name: string): string {
    return String(name || '').toLowerCase().replace(/(^|[\s\-\/])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
  }

  async createUnitFromBrreg(u: any) {
    if (u.card) return;
    const name = this.niceName(u.name);
    if (!confirm(`Create a customer card for "${name}" (org. ${u.orgId}) under ${this.data.name}?\n\nIt gets this card's PowerOffice Kundenr, references and VAT, so its invoices post to the same ledger.`)) return;
    const d = this.data;
    const card: any = {
      name, orgId: u.orgId, customerType: d.customerType || 'Company', category: d.category || null,
      address: u.address, address2: u.address2, zipCode: u.zipCode, city: u.city, country: u.country,
      powerOfficeCustomerNo: d.powerOfficeCustomerNo || '', invoiceToParent: !!d.powerOfficeCustomerNo, ourReference: d.ourReference || '',
      vatRate: d.vatRate ?? null, ownerId: d.ownerId || null, ownerName: d.ownerName || null,
      parentKundekortId: this.id,
    };
    Object.keys(card).forEach(k => card[k] === undefined && delete card[k]);
    this.savingOrg = true;
    try {
      // Different org number from the parent, so the duplicate-org guard only trips on a real duplicate.
      await this.kundeKortService.addCustomer(card, true);
      u.card = { name };
      u.isUnit = true;
      this.notifier.showNotification('success', `${name} created`);
    } catch (err: any) {
      this.notifier.showNotification('error', err?.message || 'Could not create the card');
    }
    this.savingOrg = false;
  }

  linkExistingUnit(u: any) {
    if (!u.card?.kundekortId || u.card.parentKundekortId) return;
    this.kundeKortService.updateCustomer(u.card.kundekortId, { parentKundekortId: this.id })
      .then(() => { u.isUnit = true; this.notifier.showNotification('success', `${u.card.name} added`); })
      .catch(() => this.notifier.showNotification('error', 'Could not add the unit'));
  }

  // ======================================================================
  // Billing
  // ======================================================================

  private async loadBillingHistory() {
    try {
      const [docs, companies] = await Promise.all([
        this.avstemming.listAllOnce(),
        this.membershipSvc.getCompaniesMapOnce(),
      ]);
      this.avstemmingDocs = docs;
      const multiCompany = new Set(docs.filter(d => (d.customers || []).some((c: any) => c.customerId === this.id)).map(d => d.companyId)).size > 1;
      this.billingHistory = docs
        .map(d => {
          const row = (d.customers || []).find((c: any) => c.customerId === this.id);
          if (!row) return null;
          return {
            key: d.id,
            month: d.period?.month || '',
            company: multiCompany ? (companies[d.companyId] || d.companyId) : null,
            subsidy: Number(row.confirmedSubsidyExVAT) || 0,
            credit: Number(row.creditConsumed) || 0,
            creditPant: Number(row.creditPantTotal) || 0,
            total: Number(row.billableTotal) || 0,
            flagged: Number(row.flaggedCount) || 0,
            groups: row.groups || [],
            savedBy: d.createdBy || null,
            savedAt: d.updatedAt || d.createdAt || null,
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.month.localeCompare(a.month));
      this.lastBilled = this.billingHistory[0] || null;
      this.rebuildOrg();
    } catch (err) {
      console.error('Error loading billing history', err);
    }
    this.billingLoaded = true;
  }

  toggleMonth(b: any) {
    if (b.groups.length > 1) this.expandedMonth[b.key] = !this.expandedMonth[b.key];
  }

  monthLabel(month: string): string {
    return month ? moment(month, 'YYYY-MM').format('MMM YYYY') : '';
  }

  private recomputeDriftFee() {
    this.driftFeeMonthly = this.data.driftkostnaderEnabled === false ? 0 : DRIFT_FEE_EX_VAT;
  }

  private recomputeAvtaler() {
    this.avtaleBySystem = {};
    this.avtaleMonthlyBySystem = {};
    let total = 0;
    const thisMonth = moment().format('YYYY-MM');
    this.connectedSystems.forEach(sys => {
      const keys: string[] = sys.avtaleKeys || [];
      const lines = this.avtalelinjer
        .filter(l => keys.includes(String(l.systemId)))
        .sort((a, b) => (a.kind || '').localeCompare(b.kind || ''));
      this.avtaleBySystem[sys.avtaleKey] = lines;
      // Only what is due THIS month, so the figure matches the invoice.
      let sysTotal = 0;
      lines.forEach(l => { if (AvtalelinjeService.dueInMonth(l, thisMonth)) { sysTotal += Number(l.amountExVat) || 0; } });
      this.avtaleMonthlyBySystem[sys.avtaleKey] = +sysTotal.toFixed(2);
      total += sysTotal;
    });
    this.avtaleMonthlyTotal = +total.toFixed(2);
  }

  avtaleLinesFor(sys: any): Avtalelinje[] {
    return this.avtaleBySystem[sys.avtaleKey] || [];
  }

  avtaleKindLabel(kind: string): string {
    const k = AVTALE_KINDS.find(x => x.kind === kind);
    return k ? k.label : (kind || '');
  }

  intervalLabel(interval: string): string {
    return interval === 'monthly' ? 'per month' : interval === 'quarterly' ? 'per quarter' : 'per year';
  }

  startAvtale(sys: any) {
    this.avtaleOpen[sys.avtaleKey] = true;
    this.avtaleError[sys.avtaleKey] = '';
    if (!this.avtaleDraft[sys.avtaleKey]) {
      this.avtaleDraft[sys.avtaleKey] = {
        kind: 'leie', description: '', amountExVat: null,
        interval: 'monthly', startMonth: moment().format('YYYY-MM'), endMonth: ''
      };
    }
  }

  cancelAvtale(sys: any) {
    this.avtaleOpen[sys.avtaleKey] = false;
    this.avtaleError[sys.avtaleKey] = '';
  }

  onAvtaleKindChange(sys: any) {
    const d = this.avtaleDraft[sys.avtaleKey];
    const k = AVTALE_KINDS.find(x => x.kind === d.kind);
    // Only prefill an untouched description, so a typed one is never clobbered.
    if (k && !String(d.description || '').trim()) { d.description = k.defaultDescription; }
  }

  async saveAvtale(sys: any) {
    const key = sys.avtaleKey;
    const d = this.avtaleDraft[key];
    this.avtaleError[key] = '';
    if (!(Number(d.amountExVat) > 0)) { this.avtaleError[key] = 'Amount must be greater than 0.'; return; }
    if (!d.startMonth || !/^\d{4}-\d{2}$/.test(d.startMonth)) { this.avtaleError[key] = 'Start month must look like 2026-09.'; return; }
    if (d.endMonth && !/^\d{4}-\d{2}$/.test(d.endMonth)) { this.avtaleError[key] = 'End month must look like 2026-12.'; return; }
    const kind = AVTALE_KINDS.find(x => x.kind === d.kind);
    this.avtaleSaving[key] = true;
    try {
      await this.avtaleSvc.add({
        systemId: key,
        kind: d.kind,
        description: (d.description || (kind ? kind.defaultDescription : '')).trim(),
        amountExVat: +Number(d.amountExVat).toFixed(2),
        interval: d.interval || 'monthly',
        startMonth: d.startMonth,
        endMonth: d.endMonth || null,
        active: true
      });
      this.avtaleDraft[key] = null;
      this.avtaleOpen[key] = false;
      this.notifier.showNotification('success', 'Contract charge added');
    } catch (err: any) {
      this.avtaleError[key] = 'Could not save: ' + ((err && err.message) || 'unknown error');
    }
    this.avtaleSaving[key] = false;
  }

  async toggleAvtale(line: Avtalelinje) {
    this.avtaleSaving[line.dbid] = true;
    try { await this.avtaleSvc.update(line.dbid, { active: line.active === false }); }
    catch (err) { console.error('Error toggling contract charge', err); }
    this.avtaleSaving[line.dbid] = false;
  }

  async removeAvtale(line: Avtalelinje) {
    if (!confirm(`Delete "${line.description}"? It will stop appearing on invoices from now on — months already invoiced are unaffected.`)) { return; }
    this.avtaleSaving[line.dbid] = true;
    try { await this.avtaleSvc.remove(line.dbid); }
    catch (err) { console.error('Error deleting contract charge', err); }
    this.avtaleSaving[line.dbid] = false;
  }

  // ======================================================================
  // Activity: notes, meetings, service agreements, tasks and emails in one
  // feed, with open tasks pinned above it.
  // ======================================================================

  toMillis(value: any): number {
    if (!value) return 0;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    return new Date(value).getTime();
  }

  private loadActivity() {
    this.subs.push(combineLatest([
      this.kundeKortService.getNotes(this.id),
      this.kundeKortService.getDriftAvtaler(this.id),
      this.kundeKortService.getMoteReferat(this.id),
      this.taskService.getTasksByKundekortId(this.id),
      this.emailService.getEmailsByKundekortId(this.id),
    ]).subscribe(([notes, agreements, meetings, tasks, emails]) => {
      const items: any[] = [];
      (notes as any[]).forEach(n => items.push({
        type: 'note', dbid: n.dbid, icon: 'sticky_note_2', date: this.toMillis(n.createdAt),
        title: 'Note', text: n.text, by: n.createdBy,
        extra: n.reminderDate ? `Reminder ${moment(this.toMillis(n.reminderDate)).format('D MMM YYYY HH:mm')}` : null,
      }));
      (agreements as any[]).forEach(a => items.push({
        type: 'agreement', dbid: a.dbid, icon: 'fact_check', date: this.toMillis(a.createdAt),
        title: 'Service agreement', text: a.text, by: a.createdBy,
      }));
      (meetings as any[]).forEach(m => items.push({
        type: 'meeting', dbid: m.dbid, icon: 'event_note', date: this.toMillis(m.meetingDate || m.createdAt),
        title: 'Meeting note', text: m.text, by: m.createdBy,
      }));
      (tasks as any[]).forEach(t => items.push({
        type: 'task', dbid: t.dbid, icon: t.status === 'done' ? 'task_alt' : 'checklist',
        date: this.toMillis(t.createdAt),
        title: t.status === 'done' ? 'Task completed' : 'Task created',
        text: t.title, by: t.createdBy,
        extra: t.assigneeName ? `Assigned to ${t.assigneeName}` : null,
      }));
      (emails as any[]).forEach(e => items.push({
        type: 'email', dbid: e.dbid, icon: 'email', date: e.createdAtUnix ? e.createdAtUnix * 1000 : 0,
        title: e.status === 'sent' ? 'Email sent' : 'Email scheduled',
        text: e.subject, by: e.emailTo,
      }));
      this.activityFeed = items.sort((a, b) => b.date - a.date);

      const now = Date.now();
      this.openTasks = (tasks as any[])
        .filter(t => t.status !== 'done')
        .map(t => ({ ...t, due: this.toMillis(t.dueDate), overdue: this.toMillis(t.dueDate) && this.toMillis(t.dueDate) < now }))
        .sort((a, b) => a.due - b.due);

      this.activityCounts = { all: items.length };
      items.forEach(i => this.activityCounts[i.type] = (this.activityCounts[i.type] || 0) + 1);
      this.applyActivityFilter();
    }));
  }

  setActivityFilter(key: 'all' | ActivityType) {
    this.activityFilter = key;
    this.applyActivityFilter();
  }

  private applyActivityFilter() {
    this.filteredActivity = this.activityFilter === 'all'
      ? this.activityFeed
      : this.activityFeed.filter(i => i.type === this.activityFilter);
  }

  removeActivity(item: any) {
    if (!confirm(`Delete this ${item.title.toLowerCase()}?`)) return;
    const del = item.type === 'agreement' ? this.kundeKortService.deleteDriftAvtale(item.dbid)
      : item.type === 'meeting' ? this.kundeKortService.deleteMoteReferat(item.dbid)
      : item.type === 'task' ? this.taskService.deleteTask(item.dbid)
      : null;
    del?.catch(err => { console.error('Error deleting activity', err); this.notifier.showNotification('error', 'Could not delete'); });
  }

  addNote() {
    this.dialog.open(AddNotaterDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, createdBy: this.currentUserEmail, kundeName: this.data.name } });
  }

  addTask() {
    this.dialog.open(AddTaskDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, createdBy: this.currentUserEmail, defaultAssigneeId: this.currentUserId } });
  }

  addMoteReferat() {
    this.dialog.open(AddMoteReferatDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, createdBy: this.currentUserEmail } });
  }

  addDriftAvtale() {
    this.dialog.open(AddDriftAvtaleDialog, { ...SUB_DIALOG, data: { kundekortId: this.id, createdBy: this.currentUserEmail } });
  }

  toggleTaskStatus(task: any) {
    const action = task.status === 'done' ? this.taskService.reopenTask(task.dbid) : this.taskService.completeTask(task.dbid);
    action.catch(err => console.error('Error updating task status', err));
  }

  // ======================================================================
  // Files
  // ======================================================================

  removeAttachment(att: { name: string, dbid?: string }) {
    if (!att.dbid) return;
    if (!confirm(`Delete "${att.name}"?`)) return;
    this.kundeKortService.deleteAttachment(att.dbid)
      .catch(err => console.error('Error deleting attachment', err));
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    Array.from(files).forEach(file => {
      const filePath = `attachments/${uuidv4()}_${file.name}`;
      const fileRef = this.storage.ref(filePath);
      const task = this.storage.upload(filePath, file);
      task.percentageChanges().subscribe(progress => this.uploadProgress = progress ?? 0);
      task.snapshotChanges().pipe(finalize(() => {
        fileRef.getDownloadURL().subscribe(url => {
          // The live attachments subscription picks the new doc up.
          this.kundeKortService.saveAttachments({ kundekortId: this.id, name: file.name, url })
            .catch(err => console.error('Error saving attachment', err));
          this.uploadProgress = 0;
        });
      })).subscribe();
    });
    event.target.value = '';
  }
}
